import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Image upload field — wraps a URL input with an "Upload" button that posts
 * a file to /api/admin/upload and writes back the returned URL.
 */
export default function ImageUploadField({ label = "Image URL", value, onChange, testId = "image-upload" }) {
  const { headers } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId}>
      <label className="overline text-graphite/55">{label}</label>
      <div className="flex items-end gap-3 mt-1">
        <input className="edit-input flex-1" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/cms-data/..." />
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="cec-btn !py-2 !px-3 text-xs"
          data-testid={`${testId}-button`}
        >
          <Upload size={13} /> {busy ? "Uploading…" : "Upload"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="p-2 text-graphite/55 hover:text-red-700" aria-label="Clear" data-testid={`${testId}-clear`}>
            <X size={14} />
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
      </div>
      {value && (
        <div className="mt-3">
          <img src={value} alt="Preview" className="max-h-24 object-contain bw-portrait" onError={(e) => { e.target.style.display = "none"; }} />
        </div>
      )}
    </div>
  );
}
